import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import { Plus, Search, X } from "lucide-react";

import { Variant } from "./Types";

interface ButtonProps {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  variant: Variant;
  large?: boolean;
  label?: string | React.ReactNode;
  icon?: React.ReactNode;
  active?: boolean;
  activeClassName?: string;
  isMobileToolbarButton?: boolean;
  disabled?: boolean;
  justifyBetweenIcon?: boolean;
  groupHover?: boolean;
  iconBeforeText?: boolean;
  textLeft?: boolean;
  type?: "button" | "submit";
}

const Button = ({
  label,
  variant,
  large,
  icon,
  onClick,
  active,
  activeClassName,
  isMobileToolbarButton,
  disabled,
  justifyBetweenIcon,
  groupHover,
  iconBeforeText = false,
  textLeft = false,
  type = "button",
}: ButtonProps) => {
  if (isMobileToolbarButton) {
    return <MobileToolbarButton icon={icon} onClick={onClick} />;
  }

  return (
    <button
      type={type}
      className={cn(
        "touch-device:p-0 touch-device:m-0",
        large && "w-full",
        groupHover && "group",
      )}
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      disabled={disabled}
    >
      <div
        className={cn(
          "flex flex-row px-2 h-9 gap-x-1 items-center rounded text-sm whitespace-nowrap group shadow-soft-dark-bottom",
          icon && !label && "w-9",
          textLeft && iconBeforeText
            ? "justify-start"
            : justifyBetweenIcon || textLeft
              ? "justify-between"
              : "justify-center",
          large ? "touch-device:h-9" : "touch-device:h-7",
          variant === "primary" &&
            "bg-primary-action-default desktop:hover:bg-primary-action-hover text-white",
          variant === "secondary" &&
            (active
              ? "bg-action-secondary-active text-dark-primary"
              : "bg-action-secondary-default desktop:hover:bg-action-secondary-hover text-dark-primary hover:text-dark-primary"),
          variant === "tertiary" &&
            (active
              ? (activeClassName ?? "bg-action-tertiary-active text-white")
              : "bg-action-tertiary text-dark-secondary hover:text-white desktop:hover:bg-action-tertiary-hover"),
          variant === "disabled" &&
            "bg-disabled text-dark-disabled cursor-default",
        )}
      >
        {iconBeforeText && icon && <span>{icon}</span>}
        {label && <span>{label}</span>}
        {!iconBeforeText && icon && <span>{icon}</span>}
      </div>
    </button>
  );
};

interface MobileToolbarButtonProps {
  icon: React.ReactNode;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const MobileToolbarButton = ({ icon, onClick }: MobileToolbarButtonProps) => {
  return (
    <button
      className="p-2 -mx-2 touch-device:p-0 touch-device:m-0"
      onClick={onClick}
    >
      <span className="flex h-9 flex-row px-2 py-1 -mx-1 -my-1 gap-x-1 items-center rounded text-sm whitespace-nowrap justify-center group text-gray-300">
        {icon}
      </span>
    </button>
  );
};

interface SearchButtonProps extends ButtonProps {
  isExpanded: boolean;
  searchQuery?: string;
  onClose: () => void;
  onChange: (value: string) => void;
}

export const SearchButton = ({
  isExpanded,
  variant,
  label,
  onClick,
  searchQuery,
  onChange,
  onClose,
}: SearchButtonProps) => {
  const { t } = useTranslation();
  return (
    <>
      {isExpanded ? (
        <div className="flex flex-row items-center justify-center bg-new-gray-650 px-2 py-2 cursor-pointer shadow-lg rounded-lg">
          <Search className="h-5 w-5 text-gray-400 mr-2 shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder={t("addPlayer")}
            className="bg-new-gray-650 outline-none w-full placeholder:text-gray-400 h-full text-base mobile:text-sm"
            autoFocus
          />
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="text-gray-400 ml-2 hover:text-gray-400 transition-colors shrink-0"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      ) : (
        <Button
          label={label}
          variant={variant}
          icon={<Plus className="h-5 w-5" />}
          onClick={onClick}
        />
      )}
    </>
  );
};

export const CTAButton = ({
  onClick,
  label,
  disabled,
}: {
  onClick: () => void;
  label: string;
  disabled?: boolean;
}) => {
  return (
    <button
      className={cn(
        "flex w-full h-14 px-4 py-2 justify-center items-center gap-2 rounded font-oswald text-xl",
        disabled
          ? "bg-disabled text-dark-disabled cursor-default"
          : "bg-primary-action-default text-white shadow-button-new",
      )}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {label}
    </button>
  );
};
/* 
  
  display: flex;
width: 236px;
height: 56px;
padding: 0 59px;
justify-content: center;
align-items: center;
gap: 10px;
Style

1
2
border-radius: 4px;
background: var(--Action-Button-Primary-Default, #0284C7);
  */

export default Button;
